import * as Yup from 'yup'
import {
  PencilSimple,
  Hamburger,
  Tent,
  Wine,
  Pizza,
  GameController,
  Barbell,
  Thermometer,
  WashingMachine,
  Lock,
  Suitcase,
  PawPrint,
  Toilet,
  Shower,
  Bathtub,
  Towel,
  Drop,
  WifiHigh,
  SwimmingPool,
  Broom,
  Cheers,
  UserGear,
  BeerBottle,
  Coffee,
  Tree,
  HandHeart,
  ChefHat,
  Television,
  Fan,
  Desk,
  HairDryer,
  TShirt,
  ChargingStation,
  Crown,
  Bread,
  Fire,
  Cigarette,
  Joystick,
  LetterCircleP,
  Car,
  BabyCarriage,
} from '@phosphor-icons/react'
import { startOfDay, addMinutes, setHours, setMinutes } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'

import { APP_COLORS } from '../../../../helpers/constants'
import {
  getMinutesBetweenDates,
  convertToBuildingTimestamp,
  isFile,
  cutTooLong,
} from '../../../../helpers/helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import { formValidation } from '../../../FormLegacy/validation'

export const MAX_SQUARE_FEET = 20000
export const MAX_CAPACITY = 1000
export const MAX_MIN_TIME = 1000
export const MAX_WEEKLY_LIMITER = 99
export const MAX_BOOKING_WINDOW_LIMITER = 99
export const ONE_HUNDRED_MB = 100 * 1000000 // 100MB
// Persisted amenity files are stored under a generated (uuid) filename, so the original
// name is not available. Show a friendly label for already-uploaded Overview/Agreement files.
export const AMENITY_OVERVIEW_FILE_LABEL = 'Amenity Overview.pdf'
export const AMENITY_AGREEMENT_FILE_LABEL = 'Amenity Agreement.pdf'
export const DEFAULT_TIME_INTERVAL = 30
export const MAX_FEE_NAME_LENGTH = 30
export const MAX_RESERVATION_LENGTH = 400
const FILE_FIELDS = ['overviewFile', 'agreementFile']
const MIDNIGHT_TIME_DISPLAY = 1439 // 11:59PM for display
const MIDNIGHT_TIME_STORED = 1440 // 12:00AM (midnight) for storage

export const AMENITY_TYPES = {
  MULTIDAY: 'multiday',
  HOURLY: 'hourly',
}

export const getAmenityTypeOptions = (t) => [
  { value: AMENITY_TYPES.MULTIDAY, label: t('reservations:amenityForms.types.multiday') },
  { value: AMENITY_TYPES.HOURLY, label: t('reservations:amenityForms.types.hourly') },
]

export const getAmenityCategories = (t) => [
  { value: 'Guest Suites', label: t('reservations:amenityForms.categories.guestSuites') },
  { value: 'Event Spaces', label: t('reservations:amenityForms.categories.eventSpaces') },
  { value: 'Coworking Offices', label: t('reservations:amenityForms.categories.coworkingOffices') },
  { value: 'Pools', label: t('reservations:amenityForms.categories.pools') },
  { value: 'Fitness Centers', label: t('reservations:amenityForms.categories.fitnessCenters') },
  { value: 'Storage', label: t('reservations:amenityForms.categories.storage') },
  { value: 'Parking', label: t('reservations:amenityForms.categories.parking') },
  { value: 'Yoga Studios', label: t('reservations:amenityForms.categories.yogaStudios') },
  { value: 'Sports Courts', label: t('reservations:amenityForms.categories.sportsCourts') },
  { value: 'Spas', label: t('reservations:amenityForms.categories.spas') },
  { value: 'Podcast Studios', label: t('reservations:amenityForms.categories.podcastStudios') },
  { value: 'BBQ Areas', label: t('reservations:amenityForms.categories.bbqAreas') },
  { value: 'Playgrounds', label: t('reservations:amenityForms.categories.playgrounds') },
  {
    value: 'Theaters / Screening Rooms',
    label: t('reservations:amenityForms.categories.theatersScreeningRooms'),
  },
  { value: 'Art Studios', label: t('reservations:amenityForms.categories.artStudios') },
  { value: 'Game Rooms', label: t('reservations:amenityForms.categories.gameRooms') },
  {
    value: 'Operational Facilities',
    label: t('reservations:amenityForms.categories.operationalFacilities'),
  },
  { value: 'Other', label: t('reservations:amenityForms.categories.other') },
]

export const getAmenityMarketplaceCategories = (t) => [
  { value: 'guest-suite', label: t('reservations:amenityForms.categories.guestSuites') },
  { value: 'fitness-center', label: t('reservations:amenityForms.categories.fitnessCenters') },
  { value: 'coworking-office', label: t('reservations:amenityForms.categories.coworkingOffices') },
  { value: 'event-space', label: t('reservations:amenityForms.categories.eventSpaces') },
  { value: 'pool', label: t('reservations:amenityForms.categories.pools') },
]

export const getBenefits = (t) => [
  {
    id: 'BBQ Facilities',
    text: t('reservations:amenityForms.benefits.bbqFacilities'),
    icon: Hamburger,
  },
  { id: 'Cabana', text: t('reservations:amenityForms.benefits.cabana'), icon: Tent },
  { id: 'Drink Services', text: t('reservations:amenityForms.benefits.drinkServices'), icon: Wine },
  { id: 'Food Services', text: t('reservations:amenityForms.benefits.foodServices'), icon: Pizza },
  { id: 'Game Room', text: t('reservations:amenityForms.benefits.gameRoom'), icon: GameController },
  { id: 'Gym', text: t('reservations:amenityForms.benefits.gym'), icon: Barbell },
  { id: 'Hot Tub', text: t('reservations:amenityForms.benefits.hotTub'), icon: Thermometer },
  {
    id: "Kids' Play Area",
    text: t('reservations:amenityForms.benefits.kidsPlayArea'),
    icon: Joystick,
  },
  {
    id: 'Laundry Services',
    text: t('reservations:amenityForms.benefits.laundryServices'),
    icon: WashingMachine,
  },
  { id: 'Lockers', text: t('reservations:amenityForms.benefits.lockers'), icon: Lock },
  {
    id: 'Luggage Storage',
    text: t('reservations:amenityForms.benefits.luggageStorage'),
    icon: Suitcase,
  },
  {
    id: 'Free Parking',
    text: t('reservations:amenityForms.benefits.freeParking'),
    icon: LetterCircleP,
  },
  {
    id: 'Pet-Friendly Areas',
    text: t('reservations:amenityForms.benefits.petFriendlyAreas'),
    icon: PawPrint,
  },
  { id: 'Restroom', text: t('reservations:amenityForms.benefits.restroom'), icon: Toilet },
  { id: 'Sauna', text: t('reservations:amenityForms.benefits.sauna'), icon: Thermometer },
  { id: 'Showers', text: t('reservations:amenityForms.benefits.showers'), icon: Shower },
  { id: 'Spa', text: t('reservations:amenityForms.benefits.spa'), icon: Bathtub },
  {
    id: 'Sports Courts',
    text: t('reservations:amenityForms.benefits.sportsCourts'),
    icon: Barbell,
  },
  { id: 'Towels', text: t('reservations:amenityForms.benefits.towels'), icon: Towel },
  { id: 'Waterslides', text: t('reservations:amenityForms.benefits.waterslides'), icon: Drop },
  { id: 'Wi-Fi', text: t('reservations:amenityForms.benefits.wifi'), icon: WifiHigh },
  { id: 'Pool', text: t('reservations:amenityForms.benefits.pool'), icon: SwimmingPool },
  {
    id: 'Fitness Center',
    text: t('reservations:amenityForms.benefits.fitnessCenter'),
    icon: Barbell,
  },
  { id: 'Valet Parking', text: t('reservations:amenityForms.benefits.valetParking'), icon: Car },
  { id: 'Housekeeping', text: t('reservations:amenityForms.benefits.housekeeping'), icon: Broom },
  { id: 'Bar/Lounge', text: t('reservations:amenityForms.benefits.barLounge'), icon: Cheers },
  {
    id: 'Concierge Services',
    text: t('reservations:amenityForms.benefits.conciergeServices'),
    icon: UserGear,
  },
  { id: 'Mini-bar', text: t('reservations:amenityForms.benefits.miniBar'), icon: BeerBottle },
  { id: 'Coffee Shop', text: t('reservations:amenityForms.benefits.coffeeShop'), icon: Coffee },
  { id: 'Garden', text: t('reservations:amenityForms.benefits.garden'), icon: Tree },
  {
    id: 'Massage Services',
    text: t('reservations:amenityForms.benefits.massageServices'),
    icon: HandHeart,
  },
  { id: 'Kitchen', text: t('reservations:amenityForms.benefits.kitchen'), icon: ChefHat },
  { id: 'TV', text: t('reservations:amenityForms.benefits.tv'), icon: Television },
  { id: 'Washer', text: t('reservations:amenityForms.benefits.washer'), icon: WashingMachine },
  { id: 'Dryer', text: t('reservations:amenityForms.benefits.dryer'), icon: WashingMachine },
  {
    id: 'Air Conditioning Heating',
    text: t('reservations:amenityForms.benefits.airConditioningHeating'),
    icon: Fan,
  },
  {
    id: 'Dedicated Workspace',
    text: t('reservations:amenityForms.benefits.dedicatedWorkspace'),
    icon: Desk,
  },
  { id: 'Hair Dryer', text: t('reservations:amenityForms.benefits.hairDryer'), icon: HairDryer },
  { id: 'Iron', text: t('reservations:amenityForms.benefits.iron'), icon: TShirt },
  {
    id: 'EV charger',
    text: t('reservations:amenityForms.benefits.evCharger'),
    icon: ChargingStation,
  },
  { id: 'Crib', text: t('reservations:amenityForms.benefits.crib'), icon: BabyCarriage },
  {
    id: 'King-size bed',
    text: t('reservations:amenityForms.benefits.kingSizeBed'),
    icon: Crown,
  },
  { id: 'Breakfast', text: t('reservations:amenityForms.benefits.breakfast'), icon: Bread },
  {
    id: 'Indoor fireplace',
    text: t('reservations:amenityForms.benefits.indoorFireplace'),
    icon: Fire,
  },
  {
    id: 'Smoking Allowed',
    text: t('reservations:amenityForms.benefits.smokingAllowed'),
    icon: Cigarette,
  },
]

export const getTimeIntervalOptions = (t) => [
  { value: 15, label: t('reservations:singleReservationAmenityForm.minutesOption', { count: 15 }) },
  { value: 30, label: t('reservations:singleReservationAmenityForm.minutesOption', { count: 30 }) },
  { value: 60, label: t('reservations:singleReservationAmenityForm.minutesOption', { count: 60 }) },
  { value: 90, label: t('reservations:singleReservationAmenityForm.minutesOption', { count: 90 }) },
  {
    value: 120,
    label: t('reservations:singleReservationAmenityForm.minutesOption', { count: 120 }),
  },
  {
    value: 180,
    label: t('reservations:singleReservationAmenityForm.minutesOption', { count: 180 }),
  },
  {
    value: 240,
    label: t('reservations:singleReservationAmenityForm.minutesOption', { count: 240 }),
  },
  {
    value: 360,
    label: t('reservations:singleReservationAmenityForm.minutesOption', { count: 360 }),
  },
]

export const getReservationLengthOptions = (timeInterval, hourlySlots, t) => {
  if (!hourlySlots || hourlySlots.length === 0 || !timeInterval) {
    return getTimeIntervalOptions(t).filter((option) => option.value % timeInterval === 0)
  }

  const longestWindowDuration = Math.max(
    ...hourlySlots.map((slot) => slot.endTime - slot.startTime),
  )
  const maxSlots = Math.floor(longestWindowDuration / timeInterval)

  const options = []
  for (let i = 1; i <= maxSlots; i++) {
    options.push({
      value: i * timeInterval,
      label: t('reservations:singleReservationAmenityForm.minutesOption', {
        count: i * timeInterval,
      }),
    })
  }
  return options
}

export const getTimeUnitOptions = (t) => [
  { label: t('reservations:amenityForms.hours'), value: 'hours' },
  { label: t('reservations:amenityForms.days'), value: 'days' },
]

export const getPolicyTemplates = (t) => [
  {
    name: t('reservations:amenityForms.policyTemplates.flexible.name'),
    description: t('reservations:amenityForms.policyTemplates.flexible.description'),
  },
  {
    name: t('reservations:amenityForms.policyTemplates.moderate.name'),
    description: t('reservations:amenityForms.policyTemplates.moderate.description'),
  },
  {
    name: t('reservations:amenityForms.policyTemplates.semiStrict.name'),
    description: t('reservations:amenityForms.policyTemplates.semiStrict.description'),
  },
]

export const getAfterBookingTemplates = (t) => [
  {
    name: t('reservations:amenityForms.afterBookingTemplates.useDefault.name'),
    description: t('reservations:amenityForms.afterBookingTemplates.useDefault.description'),
  },
]

const TIME_FIELDS_TO_CONVERT = ['startDate', 'endDate']

export const initialValues = {
  multiDayBooking: false,
  hidden: false,
  capacity: undefined,
  isSignatureRequired: false,
  timeInterval: undefined,
  availableStartTime: undefined,
  availableEndTime: undefined,
  extraFeeEnabled: false,
  extraFee: undefined,
  extraFeeName: '',
  description: '',
  hourlySlots: [],
  minTime: undefined,
  maxTime: undefined,
  noticePeriod: undefined,
  noticePeriodUnit: 'days',
  autoApproval: true,
  weeklyLimiter: undefined,
  showWeeklyLimiter: false,
  isOnePerDay: false,
  limitDailyReservations: false,
  multidaySlots: [],
  checkInTime: null,
  checkOutTime: null,
  startDate: null,
  endDate: null,
  specialPricing: false,
  specialPricingSlots: [],
  minimumDays: undefined,
  maximumDays: undefined,
  showQuestions: false,
  questions: [],
  spaceType: '',
  benefits: [],
  squareFeet: undefined,
  maxGuests: undefined,
  daysAvailable: [],
}

export const initialMarketplaceValues = {
  title: '',
  category: '',
  cancellationPolicy: '',
  isActive: true,
  mustSignAgreement: false,
  showCustomFee: false,
  customFeeName: null,
  customFeeAmount: null,
  overrideAvailableDays: false,
  description: '',
  nextStepsAfterBooking: '',
  maxStayLength: null,
  customMessages: [],
  agreementFile: null,
  overviewFile: null,
  showQuestions: false,
  questions: [],
}

export const onChangeNumberField = (e, updateValue) => {
  const value = e.target.value
  const max = parseInt(e.target.max) || Number.MAX_SAFE_INTEGER
  const min = parseInt(e.target.min) || Number.MIN_SAFE_INTEGER

  if (isNaN(value)) {
    e.target.value = ''
    updateValue(e)
    return
  }

  if (value > max) {
    e.target.value = max
    updateValue(e)
    return
  }

  if (value < min) {
    e.target.value = min
    updateValue(e)
    return
  }

  updateValue(e)
}

export const getImageUrl = (image) => {
  if (!image) return getAssetLink({ path: 'chitown-building', file: 'default_amenity.png' })
  if (typeof image === 'string')
    return getAssetLink({
      path: 'chitown-building',
      file: image,
      fallback: { file: 'default_amenity.png' },
    })
  return URL.createObjectURL(image)
}

export const dateChangeRaw = (e) => e.preventDefault()

const isMidnightTime = (timeValue) => {
  if (!timeValue) return false
  if (typeof timeValue === 'number') {
    return timeValue === MIDNIGHT_TIME_STORED
  }
  const minutes = timeToInteger(timeValue, true) // Check as endTime
  return minutes === MIDNIGHT_TIME_STORED
}

export const isIntervalValid = (startTime, endTime, value) => {
  if (!startTime || !endTime) {
    return true
  }
  if (isMidnightTime(endTime)) {
    return true
  }
  const minutes = getMinutesBetweenDates(startTime, endTime)
  const remainder = minutes % value
  return remainder === 0
}

// Helper function to validate that all selected days have valid slots
const validateDaysHaveSlots = (daysAvailable, slots) => {
  if (!daysAvailable || daysAvailable.length === 0) return true
  if (!slots || slots.length === 0) return false

  const daysWithSlots = new Set()
  slots.forEach((slot) => {
    if (slot.weekdays && slot.weekdays.length > 0) {
      slot.weekdays.forEach((day) => daysWithSlots.add(day))
    }
  })

  return daysAvailable.every((day) => daysWithSlots.has(day))
}

// Helper function to validate hourly slots with multiple intervals per day
const validateHourlySlotsWithIntervals = (
  slots,
  timeInterval = DEFAULT_TIME_INTERVAL,
  daysAvailable = [],
) => {
  if (!slots || slots.length === 0) return false

  // First check that all selected days have slots
  if (!validateDaysHaveSlots(daysAvailable, slots)) return false

  // Group slots by day
  const slotsByDay = {}
  slots.forEach((slot) => {
    const day = slot.startDate
    if (!slotsByDay[day]) {
      slotsByDay[day] = []
    }
    slotsByDay[day].push(slot)
  })

  // Validate each day's intervals
  for (const day in slotsByDay) {
    const daySlots = slotsByDay[day].sort((a, b) => a.startTime - b.startTime)

    for (let i = 0; i < daySlots.length; i++) {
      const slot = daySlots[i]

      // Check required fields
      if (slot.startTime === null || slot.startTime === undefined) return false
      if (slot.endTime === null || slot.endTime === undefined) return false
      if (slot.price === null || slot.price === undefined || parseFloat(slot.price) < 0)
        return false

      // Check end time is after start time
      if (slot.endTime <= slot.startTime) return false

      if (slot.endTime !== MIDNIGHT_TIME_STORED) {
        const duration = slot.endTime - slot.startTime
        if (duration % timeInterval !== 0) return false
      }

      // Check for overlaps with previous interval (allow startTime to equal prevSlot.endTime)
      if (i > 0) {
        const prevSlot = daySlots[i - 1]
        if (slot.startTime < prevSlot.endTime) return false
      }
    }
  }

  return true
}

// Helper function to convert time to integer (minutes from start of day)
export const timeToInteger = (timeValue, isEndTime = false) => {
  if (!timeValue) return 0
  const date = new Date(timeValue)
  const minutes = date.getHours() * 60 + date.getMinutes()

  // Convert 11:59PM to 1440 (midnight) for endTime
  if (isEndTime && minutes === MIDNIGHT_TIME_DISPLAY) {
    return MIDNIGHT_TIME_STORED
  }

  return minutes
}

// Helper function to convert integer minutes to Date object for time fields
export const integerToTime = (minutes) => {
  if (!minutes && minutes !== 0) return ''

  // Convert 1440 (midnight) back to 11:59PM for display
  if (minutes === MIDNIGHT_TIME_STORED) {
    const date = new Date()
    date.setHours(23, 59, 0, 0)
    return date
  }

  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  const date = new Date()
  date.setHours(hours, mins, 0, 0)
  return date
}

// Step-based validation schemas
export const makeStepValidationSchemas = (t) => {
  const v = {
    coverImageRequired: t('reservations:amenityForms.validation.coverImageRequired'),
    checkInTimeRequired: t('reservations:amenityForms.validation.checkInTimeRequired'),
    checkOutTimeRequired: t('reservations:amenityForms.validation.checkOutTimeRequired'),
    timeIntervalRequired: t('reservations:amenityForms.validation.timeIntervalRequired'),
    atLeastOneDayConfigured: t('reservations:amenityForms.validation.atLeastOneDayConfigured'),
    validPricingIntervals: t('reservations:amenityForms.validation.validPricingIntervals'),
    allSelectedDaysValidPricing: t(
      'reservations:amenityForms.validation.allSelectedDaysValidPricing',
    ),
    feeAmountRequired: t('reservations:amenityForms.validation.feeAmountRequired'),
    feeMustBeGreater: t('reservations:amenityForms.validation.feeMustBeGreater'),
    required: t('reservations:validation.required'),
    minimumDaysAtLeast1: t('reservations:amenityForms.validation.minimumDaysAtLeast1'),
    minimumDaysLessThanMax: t('reservations:amenityForms.validation.minimumDaysLessThanMax'),
    maximumDaysAtLeast1: t('reservations:amenityForms.validation.maximumDaysAtLeast1'),
    maximumDaysGreaterThanMin: t('reservations:amenityForms.validation.maximumDaysGreaterThanMin'),
    minimumTimeRequired: t('reservations:amenityForms.validation.minimumTimeRequired'),
    maximumTimeRequired: t('reservations:amenityForms.validation.maximumTimeRequired'),
    weeklyLimiterRequired: t('reservations:amenityForms.validation.weeklyLimiterRequired'),
    maxBookingsPerSlotRequired: t(
      'reservations:amenityForms.validation.maxBookingsPerSlotRequired',
    ),
    capacityAtLeast1: t('reservations:amenityForms.validation.capacityAtLeast1'),
    maximumCapacity: t('reservations:amenityForms.validation.maximumCapacity', {
      max: MAX_CAPACITY,
    }),
    atLeastOneQuestionRequired: t(
      'reservations:amenityForms.validation.atLeastOneQuestionRequired',
    ),
    futureReservationLimiterRequired: t(
      'reservations:amenityForms.validation.futureReservationLimiterRequired',
    ),
    specialPricingSlotsValid: t('reservations:amenityForms.validation.specialPricingSlotsValid'),
  }

  return {
    // Step 0: Amenity Details
    0: Yup.object().shape({
      image: Yup.mixed().required(v.coverImageRequired),
      name: formValidation.required,
      description: formValidation.required,
      spaceType: formValidation.required,
    }),

    // Step 1: Pricing & Availability
    1: Yup.object().shape({
      // Conditional validation based on multiDayBooking
      checkInTime: Yup.mixed().when('multiDayBooking', {
        is: true,
        then: Yup.mixed().required(v.checkInTimeRequired),
        otherwise: Yup.mixed(),
      }),
      checkOutTime: Yup.mixed().when('multiDayBooking', {
        is: true,
        then: Yup.mixed().required(v.checkOutTimeRequired),
        otherwise: Yup.mixed(),
      }),
      timeInterval: Yup.mixed().when('multiDayBooking', {
        is: false,
        then: Yup.mixed().required(v.timeIntervalRequired),
        otherwise: Yup.mixed(),
      }),
      // Pricing validation - at least one day must be configured
      hourlySlots: Yup.array().when('multiDayBooking', {
        is: false,
        then: Yup.array()
          .min(1, v.atLeastOneDayConfigured)
          .test('valid-pricing', v.validPricingIntervals, function (value) {
            const { timeInterval, daysAvailable } = this.parent
            return validateHourlySlotsWithIntervals(
              value,
              timeInterval || DEFAULT_TIME_INTERVAL,
              daysAvailable,
            )
          }),
        otherwise: Yup.array(),
      }),
      multidaySlots: Yup.array().when(['multiDayBooking', 'specialPricing'], {
        is: (multiDay, specialPricing) => multiDay === true || specialPricing === true,
        then: Yup.array()
          .test('multiday-slots-validation', function (value) {
            const { multiDayBooking, daysAvailable } = this.parent

            if (multiDayBooking === true && (!value || value.length === 0)) {
              throw this.createError({ message: v.atLeastOneDayConfigured })
            }

            if (multiDayBooking === true && value && value.length > 0) {
              const regularSlots = value.filter((slot) => slot.repeats === true)

              if (regularSlots.length === 0) {
                throw this.createError({ message: v.atLeastOneDayConfigured })
              }

              // Check that all selected days have slots
              if (daysAvailable && daysAvailable.length > 0) {
                if (!validateDaysHaveSlots(daysAvailable, regularSlots)) {
                  throw this.createError({ message: v.allSelectedDaysValidPricing })
                }
              }

              const regularSlotsValid = regularSlots.every(
                (slot) => slot && parseFloat(slot.price) >= 0,
              )
              if (!regularSlotsValid) {
                throw this.createError({ message: v.allSelectedDaysValidPricing })
              }
            }

            return true
          }),
        otherwise: Yup.array(),
      }),
      specialPricingSlots: Yup.array().when(['specialPricing'], {
        is: (specialPricing) => (specialPricing = true),
        then: Yup.array()
          .test('valid-special-pricing', function (value) {
            const { specialPricing } = this.parent
            if (!value.length && specialPricing) {
              throw this.createError({ message: v.specialPricingSlotsValid })
            }

            return true
          }),
      }),
      startDate: Yup.mixed().nullable(),
      endDate: Yup.mixed().nullable(),
      // Extra fee validation
      extraFeeName: Yup.string().nullable().when('extraFeeEnabled', {
        is: true,
        then: formValidation.required,
        otherwise: Yup.string(),
      }),
      extraFee: Yup.mixed()
        .nullable()
        .when('extraFeeEnabled', {
          is: true,
          then: Yup.number().required(v.feeAmountRequired).min(1, v.feeMustBeGreater),
          otherwise: Yup.mixed().nullable(),
        }),
    }),

    // Step 2: Reservation Limits
    2: Yup.object().shape({
      // Multiday validation
      minimumDays: Yup.number()
        .nullable()
        .when('multiDayBooking', {
          is: true,
          then: Yup.number()
            .required(v.required)
            .min(1, v.minimumDaysAtLeast1)
            .test('min-less-than-max', v.minimumDaysLessThanMax, function (value) {
              const { maximumDays } = this.parent
              if (!maximumDays || maximumDays === 0) return true
              return parseInt(value) <= parseInt(maximumDays)
            }),
          otherwise: Yup.number(),
        }),
      maximumDays: Yup.number()
        .nullable()
        .when('multiDayBooking', {
          is: true,
          then: Yup.number()
            .required(v.required)
            .min(1, v.maximumDaysAtLeast1)
            .test('max-greater-than-min', v.maximumDaysGreaterThanMin, function (value) {
              const { minimumDays } = this.parent
              if (!minimumDays || minimumDays === 0) return true
              return parseInt(value) >= parseInt(minimumDays)
            }),
          otherwise: Yup.number(),
        }),
      // Hourly validation
      minTime: Yup.number()
        .nullable()
        .when('multiDayBooking', {
          is: false,
          then: Yup.number().required(v.minimumTimeRequired),
          otherwise: Yup.number(),
        }),
      maxTime: Yup.number()
        .nullable()
        .when('multiDayBooking', {
          is: false,
          then: Yup.number().required(v.maximumTimeRequired),
          otherwise: Yup.number(),
        }),
      // Weekly limiter validation for hourly amenities
      weeklyLimiter: Yup.number()
        .nullable()
        .when(['multiDayBooking', 'showWeeklyLimiter'], {
          is: (multiDay, showLimiter) => multiDay === false && showLimiter === true,
          then: formValidation.weeklyLimiter.required(v.weeklyLimiterRequired),
          otherwise: formValidation.weeklyLimiter,
        }),
      // Capacity validation
      capacity: Yup.number()
        .nullable()
        .when('multiDayBooking', {
          is: false,
          then: Yup.number()
            .required(v.maxBookingsPerSlotRequired)
            .min(1, v.capacityAtLeast1)
            .max(MAX_CAPACITY, v.maximumCapacity),
          otherwise: Yup.number(),
        }),
      // Questions validation
      questions: Yup.array().when('showQuestions', {
        is: true,
        then: Yup.array().min(1, v.atLeastOneQuestionRequired),
        otherwise: Yup.array(),
      }),
      // Future reservation limiter validation
      bookingWindow: Yup.number()
        .nullable()
        .when('showBookingWindow', {
          is: true,
          then: Yup.number().required(v.futureReservationLimiterRequired),
          otherwise: Yup.number(),
        }),
    }),
  }
}

// Full form validation schemas
export const makeValidationSingleReservation = (t) => {
  const v = {
    coverImageRequired: t('reservations:amenityForms.validation.coverImageRequired'),
    maxBookingsPerSlotRequired: t(
      'reservations:amenityForms.validation.maxBookingsPerSlotRequired',
    ),
    capacityAtLeast1: t('reservations:amenityForms.validation.capacityAtLeast1'),
    maximumCapacity: t('reservations:amenityForms.validation.maximumCapacity', {
      max: MAX_CAPACITY,
    }),
    timeIntervalRequired: t('reservations:amenityForms.validation.timeIntervalRequired'),
    atLeastOneDayConfigured: t('reservations:amenityForms.validation.atLeastOneDayConfigured'),
    validPricingIntervals: t('reservations:amenityForms.validation.validPricingIntervals'),
    minimumTimeRequired: t('reservations:amenityForms.validation.minimumTimeRequired'),
    maximumTimeRequired: t('reservations:amenityForms.validation.maximumTimeRequired'),
    feeAmountRequired: t('reservations:amenityForms.validation.feeAmountRequired'),
    feeMustBeGreater: t('reservations:amenityForms.validation.feeMustBeGreater'),
    weeklyLimiterRequired: t('reservations:amenityForms.validation.weeklyLimiterRequired'),
    futureReservationLimiterRequired: t(
      'reservations:amenityForms.validation.futureReservationLimiterRequired',
    ),
    atLeastOneQuestionRequired: t(
      'reservations:amenityForms.validation.atLeastOneQuestionRequired',
    ),
    specialPricingSlotsValid: t('reservations:amenityForms.validation.specialPricingSlotsValid'),
  }

  return Yup.object().shape({
    // Basic details
    image: Yup.mixed().required(v.coverImageRequired),
    name: formValidation.required,
    description: formValidation.required,
    spaceType: formValidation.required,
    capacity: Yup.number()
      .nullable()
      .when('multiDayBooking', {
        is: false,
        then: Yup.number()
          .required(v.maxBookingsPerSlotRequired)
          .min(1, v.capacityAtLeast1)
          .max(MAX_CAPACITY, v.maximumCapacity),
        otherwise: Yup.number(),
      }),

    // Hourly-specific validation
    timeInterval: Yup.mixed().when('multiDayBooking', {
      is: false,
      then: Yup.mixed().required(v.timeIntervalRequired),
      otherwise: Yup.mixed(),
    }),
    hourlySlots: Yup.array().when('multiDayBooking', {
      is: false,
      then: Yup.array()
        .min(1, v.atLeastOneDayConfigured)
        .test('valid-pricing', v.validPricingIntervals, function (value) {
          const { timeInterval, daysAvailable } = this.parent
          return validateHourlySlotsWithIntervals(
            value,
            timeInterval || DEFAULT_TIME_INTERVAL,
            daysAvailable,
          )
        }),
      otherwise: Yup.array(),
    }),
    specialPricingSlots: Yup.array().when(['specialPricing'], {
      is: (specialPricing) => (specialPricing = true),
      then: Yup.array()
        .test('valid-special-pricing', function (value) {
          const { specialPricing } = this.parent
          if (!value.length && specialPricing) {
            throw this.createError({ message: v.specialPricingSlotsValid })
          }

          return true
        }),
    }),
    minTime: Yup.number()
      .nullable()
      .when('multiDayBooking', {
        is: false,
        then: Yup.number().required(v.minimumTimeRequired),
        otherwise: Yup.number(),
      }),
    maxTime: Yup.number()
      .nullable()
      .when('multiDayBooking', {
        is: false,
        then: Yup.number().required(v.maximumTimeRequired),
        otherwise: Yup.number(),
      }),

    startDate: Yup.mixed().nullable(),
    endDate: Yup.mixed().nullable(),

    // Extra fee validation
    extraFeeName: Yup.string().nullable().when('extraFeeEnabled', {
      is: true,
      then: formValidation.required,
      otherwise: Yup.string(),
    }),
    extraFee: Yup.mixed()
      .nullable()
      .when('extraFeeEnabled', {
        is: true,
        then: Yup.number().required(v.feeAmountRequired).min(1, v.feeMustBeGreater),
        otherwise: Yup.mixed().nullable(),
      }),

    // Weekly limiter validation
    weeklyLimiter: Yup.number()
      .nullable()
      .when(['multiDayBooking', 'showWeeklyLimiter'], {
        is: (multiDay, showLimiter) => multiDay === false && showLimiter === true,
        then: formValidation.weeklyLimiter.required(v.weeklyLimiterRequired),
        otherwise: formValidation.weeklyLimiter,
      }),

    // Future reservation limiter validation
    bookingWindow: Yup.number()
      .nullable()
      .when('showBookingWindow', {
        is: true,
        then: Yup.number().required(v.futureReservationLimiterRequired),
        otherwise: Yup.number(),
      }),
    questions: Yup.array().when('showQuestions', {
      is: true,
      then: Yup.array().min(1, v.atLeastOneQuestionRequired),
      otherwise: Yup.array(),
    }),
  })
}

export const makeValidationMultidayReservation = (t) => {
  const v = {
    coverImageRequired: t('reservations:amenityForms.validation.coverImageRequired'),
    checkInTimeRequired: t('reservations:amenityForms.validation.checkInTimeRequired'),
    checkOutTimeRequired: t('reservations:amenityForms.validation.checkOutTimeRequired'),
    atLeastOneDayConfigured: t('reservations:amenityForms.validation.atLeastOneDayConfigured'),
    allSelectedDaysValidPricing: t(
      'reservations:amenityForms.validation.allSelectedDaysValidPricing',
    ),
    required: t('reservations:validation.required'),
    minimumDaysAtLeast1: t('reservations:amenityForms.validation.minimumDaysAtLeast1'),
    minimumDaysLessThanMax: t('reservations:amenityForms.validation.minimumDaysLessThanMax'),
    maximumDaysAtLeast1: t('reservations:amenityForms.validation.maximumDaysAtLeast1'),
    maximumDaysGreaterThanMin: t('reservations:amenityForms.validation.maximumDaysGreaterThanMin'),
    feeAmountRequired: t('reservations:amenityForms.validation.feeAmountRequired'),
    feeMustBeGreater: t('reservations:amenityForms.validation.feeMustBeGreater'),
    atLeastOneQuestionRequired: t(
      'reservations:amenityForms.validation.atLeastOneQuestionRequired',
    ),
    futureReservationLimiterRequired: t(
      'reservations:amenityForms.validation.futureReservationLimiterRequired',
    ),
    specialPricingSlotsValid: t('reservations:amenityForms.validation.specialPricingSlotsValid'),
  }

  return Yup.object().shape({
    // Basic details
    image: Yup.mixed().required(v.coverImageRequired),
    name: formValidation.required,
    description: formValidation.required,
    spaceType: formValidation.required,

    // Multiday-specific validation
    checkInTime: Yup.mixed().required(v.checkInTimeRequired),
    checkOutTime: Yup.mixed().required(v.checkOutTimeRequired),
    multidaySlots: Yup.array().when(['multiDayBooking', 'specialPricing'], {
      is: (multiDay, specialPricing) => multiDay === true || specialPricing === true,
      then: Yup.array()
        .test('multiday-slots-validation', function (value) {
          const { multiDayBooking, daysAvailable } = this.parent

          if (multiDayBooking === true && (!value || value.length === 0)) {
            throw this.createError({ message: v.atLeastOneDayConfigured })
          }

          if (multiDayBooking === true && value && value.length > 0) {
            const regularSlots = value.filter((slot) => slot.repeats === true)

            if (regularSlots.length === 0) {
              throw this.createError({ message: v.atLeastOneDayConfigured })
            }

            // Check that all selected days have slots
            if (daysAvailable && daysAvailable.length > 0) {
              if (!validateDaysHaveSlots(daysAvailable, regularSlots)) {
                throw this.createError({ message: v.allSelectedDaysValidPricing })
              }
            }

            const regularSlotsValid = regularSlots.every(
              (slot) => slot && parseFloat(slot.price) >= 0,
            )
            if (!regularSlotsValid) {
              throw this.createError({ message: v.allSelectedDaysValidPricing })
            }
          }

          return true
        }),
      otherwise: Yup.array(),
    }),
    specialPricingSlots: Yup.array().when(['specialPricing'], {
      is: (specialPricing) => (specialPricing = true),
      then: Yup.array()
        .test('valid-special-pricing', function (value) {
          const { specialPricing } = this.parent
          if (!value.length && specialPricing) {
            throw this.createError({ message: v.specialPricingSlotsValid })
          }

          return true
        }),
    }),
    minimumDays: Yup.number().when('multiDayBooking', {
      is: true,
      then: Yup.number()
        .required(v.required)
        .min(1, v.minimumDaysAtLeast1)
        .test('min-less-than-max', v.minimumDaysLessThanMax, function (value) {
          const { maximumDays } = this.parent
          if (!maximumDays || maximumDays === 0) return true
          return parseInt(value) <= parseInt(maximumDays)
        }),
      otherwise: Yup.number(),
    }),
    maximumDays: Yup.number().when('multiDayBooking', {
      is: true,
      then: Yup.number()
        .required(v.required)
        .min(1, v.maximumDaysAtLeast1)
        .test('max-greater-than-min', v.maximumDaysGreaterThanMin, function (value) {
          const { minimumDays } = this.parent
          if (!minimumDays || minimumDays === 0) return true
          return parseInt(value) >= parseInt(minimumDays)
        }),
      otherwise: Yup.number(),
    }),
    // Extra fee validation
    extraFeeName: Yup.string().nullable().when('extraFeeEnabled', {
      is: true,
      then: formValidation.required,
      otherwise: Yup.string(),
    }),
    extraFee: Yup.mixed()
      .nullable()
      .when('extraFeeEnabled', {
        is: true,
        then: Yup.number().required(v.feeAmountRequired).min(1, v.feeMustBeGreater),
        otherwise: Yup.mixed().nullable(),
      }),
    questions: Yup.array().when('showQuestions', {
      is: true,
      then: Yup.array().min(1, v.atLeastOneQuestionRequired),
      otherwise: Yup.array(),
    }),
    bookingWindow: Yup.number()
      .nullable()
      .when('showBookingWindow', {
        is: true,
        then: Yup.number().required(v.futureReservationLimiterRequired),
        otherwise: Yup.number(),
      }),
  })
}

export const makeValidationMarketplace = (t) => {
  const v = {
    feeAmountRequired: t('reservations:amenityForms.validation.feeAmountRequired'),
    feeMustBeGreater: t('reservations:amenityForms.validation.feeMustBeGreater'),
    atLeastOneQuestionRequired: t(
      'reservations:amenityForms.validation.atLeastOneQuestionRequired',
    ),
    atLeastOneDayConfigured: t('reservations:amenityForms.validation.atLeastOneDayConfigured'),
    allSelectedDaysValidPricing: t(
      'reservations:amenityForms.validation.allSelectedDaysValidPricing',
    ),
    validPricingIntervals: t('reservations:amenityForms.validation.validPricingIntervals'),
  }

  return Yup.object().shape({
    title: formValidation.required,
    category: formValidation.required,
    cancellationPolicy: formValidation.required,
    customFeeName: Yup.string().nullable().when('showCustomFee', {
      is: true,
      then: formValidation.required,
      otherwise: Yup.string(),
    }),
    customFeeAmount: Yup.mixed()
      .nullable()
      .when('showCustomFee', {
        is: true,
        then: Yup.number().required(v.feeAmountRequired).min(1, v.feeMustBeGreater),
        otherwise: Yup.mixed().nullable(),
      }),
    questions: Yup.array().when('showQuestions', {
      is: true,
      then: Yup.array().min(1, v.atLeastOneQuestionRequired),
      otherwise: Yup.array(),
    }),
    multidaySlots: Yup.array().when(['multiDayBooking', 'overrideAvailableDays'], {
      is: (multiDay, overrideAvailableDays) => multiDay === true && overrideAvailableDays === true,
      then: Yup.array()
        .test('multiday-slots-validation', function (value) {
          const { multiDayBooking, daysAvailable } = this.parent

          if (multiDayBooking === true && (!value || value.length === 0)) {
            throw this.createError({
              message: v.atLeastOneDayConfigured,
            })
          }

          if (multiDayBooking === true && value && value.length > 0) {
            const regularSlots = value.filter((slot) => slot.repeats === true)

            if (regularSlots.length === 0) {
              throw this.createError({
                message: v.atLeastOneDayConfigured,
              })
            }

            // Check that all selected days have slots
            if (daysAvailable && daysAvailable.length > 0) {
              if (!validateDaysHaveSlots(daysAvailable, regularSlots)) {
                throw this.createError({ message: v.allSelectedDaysValidPricing })
              }
            }

            const regularSlotsValid = regularSlots.every(
              (slot) => slot && parseFloat(slot.price) >= 0,
            )

            if (!regularSlotsValid) {
              throw this.createError({ message: v.allSelectedDaysValidPricing })
            }
          }

          return true
        }),
      otherwise: Yup.array(),
    }),
    hourlySlots: Yup.array().when(['multiDayBooking', 'overrideAvailableDays'], {
      is: (multiDay, overrideAvailableDays) => multiDay === false && overrideAvailableDays === true,
      then: Yup.array()
        .min(1, v.atLeastOneDayConfigured)
        .test('valid-pricing', v.validPricingIntervals, function (value) {
          const { timeInterval, daysAvailable } = this.parent
          return validateHourlySlotsWithIntervals(
            value,
            timeInterval || DEFAULT_TIME_INTERVAL,
            daysAvailable,
          )
        }),
      otherwise: Yup.array(),
    }),
  })
}

export const spacesColumns = ({ onEdit = () => {} }, t) => [
  {
    Header: t('reservations:amenitiesTable.image'),
    accessor: 'image',
    Cell: ({ value, row }) => (
      <div className="spaces__table-image">
        <img src={getImageUrl(value)} alt={row.original.name} />
      </div>
    ),
  },
  {
    Header: t('reservations:amenitiesTable.name'),
    accessor: 'name',
    Cell: ({ value }) => <div className="spaces__table-name">{cutTooLong(value, 35)}</div>,
  },
  {
    Header: t('reservations:amenitiesTable.description'),
    accessor: 'description',
    Cell: ({ value }) => <div className="spaces__table-description">{cutTooLong(value, 260)}</div>,
  },
  {
    Header: t('reservations:amenitiesTable.availableForBooking'),
    accessor: 'available',
    Cell: ({ value }) => (
      <div className="spaces__table-available">
        {value ? t('reservations:amenitiesTable.yes') : t('reservations:amenitiesTable.no')}
      </div>
    ),
  },
  {
    Header: t('reservations:amenityForms.action'),
    accessor: 'actions',
    Cell: ({ row }) => (
      <button className="button button-transparent" onClick={() => onEdit(row.original)}>
        <PencilSimple size={20} />
        {t('common:actions.edit')}
      </button>
    ),
  },
]

const preparePayloadTimeData = (values, timezone) => {
  const { multidaySlots = [], hourlySlots = [], ...rest } = values

  if (rest.multiDayBooking && !rest.startDate) {
    delete rest.startDate
    delete rest.endDate
  }

  if (rest.checkInTime) {
    rest.checkInTime = timeToInteger(rest.checkInTime)
  }

  if (rest.checkOutTime) {
    rest.checkOutTime = timeToInteger(rest.checkOutTime)
  }

  const multidaySlotsWithBuildingTimestamp = (multidaySlots || []).map((slot) => ({
    ...slot,
    startDate: convertToBuildingTimestamp(slot.startDate, { timezone }),
  }))
  const hourlySlotsWithBuildingTimestamp = (hourlySlots || []).map((slot) => ({
    ...slot,
    startDate: convertToBuildingTimestamp(slot.startDate, { timezone }),
  }))

  TIME_FIELDS_TO_CONVERT.forEach((field) => {
    if (rest[field]) {
      rest[field] = convertToBuildingTimestamp(rest[field], { timezone }, 0, 0)
    }
  })

  return {
    ...rest,
    multidaySlots: multidaySlotsWithBuildingTimestamp,
    hourlySlots: hourlySlotsWithBuildingTimestamp,
  }
}

const prepareFormTimeData = (values, timezone = 'America/Chicago') => {
  const { hourlySlots = [], multidaySlots = [], ...rest } = values

  if (hourlySlots?.length > 0) {
    rest.hourlySlots = hourlySlots.map((slot) => ({
      ...slot,
      startDate: setHours(
        setMinutes(toZonedTime(new Date(slot.startDate), timezone), 0),
        0,
      ).getTime(),
    }))
  }

  if (multidaySlots?.length > 0) {
    rest.multidaySlots = multidaySlots.map((slot) => ({
      ...slot,
      startDate: setHours(
        setMinutes(toZonedTime(new Date(slot.startDate), timezone), 0),
        0,
      ).getTime(),
    }))
  }

  TIME_FIELDS_TO_CONVERT.forEach((field) => {
    if (rest[field]) {
      rest[field] = setHours(
        setMinutes(toZonedTime(new Date(rest[field]), timezone), 0),
        0,
      ).getTime()
    }
  })

  if (rest.checkInTime || rest.checkInTime === 0) {
    rest.checkInTime = integerToTime(rest.checkInTime)
  }
  if (rest.checkOutTime || rest.checkOutTime === 0) {
    rest.checkOutTime = integerToTime(rest.checkOutTime)
  }

  return { ...values, ...rest }
}

export const getAmenityFormValues = (amenity, timezone = 'America/Chicago') => {
  const data = {
    ...initialValues,
    ...amenity,
  }

  data.extraFeeEnabled =
    data.extraFee !== undefined && data.extraFee !== null && !!data.extraFeeName
  data.showWeeklyLimiter = data?.weeklyLimiter > 0

  data.specialPricing = !!data?.applySpecialPricing
  data.showQuestions = data?.questions?.length > 0
  if (data?.noticePeriod % 24 === 0) {
    data.noticePeriodUnit = 'days'
    data.noticePeriod = data?.noticePeriod / 24
  } else {
    data.noticePeriodUnit = 'hours'
  }
  data.benefits = data?.benefits?.length > 0 ? data?.benefits : []

  const hourlySlots = data.hourlySlots || []
  const multidaySlots = data.multidaySlots || []

  data.hourlySlots = hourlySlots.filter((slot) => slot.repeats)
  data.multidaySlots = multidaySlots.filter((slot) => slot.repeats)

  data.specialPricingSlots = data.multiDayBooking
    ? multidaySlots.filter((slot) => !slot.repeats)
    : hourlySlots.filter((slot) => !slot.repeats)

  data.daysAvailable = data?.daysAvailable?.length > 0 ? data?.daysAvailable : []
  data.showBookingWindow = data?.bookingWindow > 0

  const formData = prepareFormTimeData(data, timezone)

  return formData
}

export const getMarketplaceFormValues = (amenityMarketplace = {}, isMultiDay) => {
  const data = {
    ...initialMarketplaceValues,
    ...amenityMarketplace,
  }

  data.showCustomFee = !!data.customFeeAmount && !!data.customFeeName
  data.showQuestions = data?.questions?.length > 0
  data.overrideAvailableDays = data?.multidaySlots?.length > 0 || data?.hourlySlots?.length > 0
  data.multidaySlots = data?.multidaySlots?.length > 0 ? data?.multidaySlots : []
  data.hourlySlots = data?.hourlySlots?.length > 0 ? data?.hourlySlots : []
  data.multiDayBooking = isMultiDay

  return data
}

export const preparePayload = (values, timezone) => {
  const data = preparePayloadTimeData(values, timezone)
  data.noticePeriod =
    data?.noticePeriodUnit === 'days' ? data?.noticePeriod * 24 : data?.noticePeriod

  if (!data.extraFeeEnabled) {
    data.extraFeeName = null
    data.extraFee = null
  }

  if (!data.showWeeklyLimiter) {
    data.weeklyLimiter = null
  }

  if (!data.showQuestions) {
    data.questions = []
  }

  if (data.multiDayBooking) {
    data.multidaySlots = [...data.multidaySlots, ...data.specialPricingSlots]
    delete data.specialPricingSlots
  } else {
    data.hourlySlots = [
      ...data.hourlySlots,
      ...data.specialPricingSlots.map((slot) => ({ ...slot, startTime: 0, endTime: 0 })),
    ]
    delete data.specialPricingSlots
  }

  data.applySpecialPricing = values.specialPricing

  if (!data.showBookingWindow) {
    data.bookingWindow = null
  }

  if (!data.agreement) {
    data.isSignatureRequired = false
  }

  Object.keys(data).forEach((key) => {
    if (data[key] === '') {
      data[key] = null
    }
  })

  delete data.locations

  return data
}

export const prepareMarketplacePayload = (values) => {
  const payload = { ...values }
  FILE_FIELDS.forEach((field) => {
    const value = values[field]
    if (isFile(value)) {
      // New upload — send the File so it is uploaded via multipart.
      payload[field] = value
    } else if (typeof value === 'string' && value) {
      // Unchanged persisted file (returned as a full URL) — don't resend it as the value.
      delete payload[field]
    } else {
      // Removed / empty — send an empty value so the backend clears the stored file.
      payload[field] = ''
    }
  })
  Object.keys(payload).forEach((key) => {
    if (payload[key] === '') {
      payload[key] = null
    }
  })

  if (!payload.showCustomFee) {
    payload.customFeeName = null
    payload.customFeeAmount = null
  }

  if (!payload.agreementFile) {
    payload.mustSignAgreement = false
  }

  return payload
}

export const getMidnightTime = () => {
  const today = new Date()
  return addMinutes(startOfDay(today), MIDNIGHT_TIME_DISPLAY)
}

export const saltoSpaceDropdownStyles = () => ({
  control: () => ({
    display: 'flex',
    minHeight: '5vh',
    backgroundColor: APP_COLORS.grey200,
    justifyContent: 'space-between',
    border: `1px solid ${APP_COLORS.grey800}`,
    borderRadius: '5px',
    alignItems: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
  }),
  option: (_, state) => ({
    backgroundColor: state.isSelected ? APP_COLORS.grey300 : APP_COLORS.baseWhite,
    flexDirection: 'row',
    display: 'flex',
    height: '5vh',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingLeft: '1vw',
    fontSize: '14px',
    cursor: 'pointer',
    borderBottom: `1px solid ${APP_COLORS.grey300}`,
  }),
  valueContainer: (styles) => ({
    ...styles,
    maxHeight: '10vh',
    overflowY: 'auto',
  }),
})

export const getSelectedItemsValue = (selectedIds, itemsArray) => {
  return selectedIds
    ? selectedIds.map((id) => itemsArray.find((item) => item.saltoId === id))
    : null
}
